import { useState } from 'react'
import type { EditField, EditValue } from '@cims-online/shared'
import type { TooltipProps } from 'antd'
import { Form, Popover, Space, Tag, Tooltip, Typography } from 'antd'
import CIMSBadge from 'components/CIMSBadge'
import CIMSSelect from 'components/CIMSSelect'
import CIMSTextArea from 'components/CIMSTextArea'
import HelpIcon from 'components/HelpIcon'
import { dateFormatter } from 'utils/dateFormatter'

type EditFieldControlProps = {
  field: EditField
  value: EditValue
  error?: string
  disabled: boolean
  onChange: (value: EditValue) => void
}

/**
 * Renders one resolved input or contextual value with shared validation errors.
 * Comment inputs show saved history as a count, while approval options expose
 * recorded state through keyboard- and pointer-accessible overlays.
 */
function EditFieldControl({
  field,
  value,
  error,
  disabled,
  onChange,
}: EditFieldControlProps) {
  const [openApproval, setOpenApproval] = useState<string | null>(null)
  if (field.hidden) return null
  const errorId = `${field.id}-error`
  let control: React.ReactNode
  if (!field.input && field.type === 'approvals' && field.approvals?.length)
    control = (
      <Space direction="vertical">
        {field.approvals.map(
          ({ optionId, label, tooltip, state, metadata }) => {
            const colors = {
              approved: 'success',
              rejected: 'error',
              cleared: undefined,
            }
            const decorated = state && state !== 'cleared' && metadata
            const open = openApproval === optionId
            const id = `${field.id}-${optionId}-context`
            const overlayProps: TooltipProps = {
              destroyTooltipOnHide: true,
              id,
              open,
              // Ant defaults to hover. These triggers also support keyboard
              // and touch interaction.
              trigger: ['hover', 'focus', 'click'],
              onOpenChange: (visible) =>
                setOpenApproval((current) => {
                  if (visible) return optionId
                  return current === optionId ? null : current
                }),
            }
            const tag = (
              <Tag color={state ? colors[state] : undefined}>{label}</Tag>
            )
            if (!tooltip && !decorated) return <span key={optionId}>{tag}</span>
            const trigger = (
              <button
                type="button"
                aria-describedby={open ? id : undefined}
                style={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  font: 'inherit',
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape' && open) {
                    event.stopPropagation()
                    setOpenApproval(null)
                  }
                }}
              >
                {tag}
              </button>
            )
            if (state && state !== 'cleared' && metadata) {
              const labels = {
                approved: 'Approved',
                rejected: 'Rejected',
              }
              const context = `${
                labels[state]
              } on ${dateFormatter.shortDatetime(metadata.date)} by ${
                metadata.username
              }`
              if (!tooltip)
                return (
                  <Tooltip key={optionId} title={context} {...overlayProps}>
                    {trigger}
                  </Tooltip>
                )
              return (
                <Popover
                  key={optionId}
                  {...overlayProps}
                  content={
                    <Space
                      direction="vertical"
                      size={4}
                      style={{ maxWidth: 360 }}
                    >
                      <Typography.Text>{context}</Typography.Text>
                      <Typography.Text
                        type="secondary"
                        style={{ whiteSpace: 'pre-wrap' }}
                      >
                        {tooltip}
                      </Typography.Text>
                    </Space>
                  }
                >
                  {trigger}
                </Popover>
              )
            }
            return (
              <Tooltip key={optionId} title={tooltip} {...overlayProps}>
                {trigger}
              </Tooltip>
            )
          }
        )}
      </Space>
    )
  else if (!field.input)
    control = (
      <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>
        {field.displayValue || '—'}
      </Typography.Paragraph>
    )
  else if (field.readOnly) {
    const display = Array.isArray(value)
      ? value
          .map(
            (id) =>
              field.options.find((option) => option.id === id)?.label ?? id
          )
          .join(', ')
      : value
    control = (
      <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>
        {display || '—'}
      </Typography.Paragraph>
    )
  } else if (field.type === 'long_text' || field.type === 'comments')
    control = (
      <CIMSTextArea
        id={field.id}
        aria-label={field.name}
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? true : undefined}
        disabled={disabled}
        placeholder={field.type === 'comments' ? 'Add a comment...' : undefined}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(event.target.value)}
      />
    )
  else {
    const selected = Array.isArray(value) ? value : []
    control = (
      <CIMSSelect
        id={field.id}
        aria-label={field.name}
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? true : undefined}
        simple
        disabled={disabled}
        mode={field.single ? undefined : 'multiple'}
        value={field.single ? selected[0] : selected}
        options={field.options.map(({ id, label, tooltip, active }) => ({
          value: id,
          label: active ? label : `${label} (removed)`,
          title: tooltip ?? undefined,
        }))}
        onChange={(selection) => {
          if (typeof selection === 'string') onChange([selection])
          else if (Array.isArray(selection))
            onChange(
              selection.filter(
                (item): item is string => typeof item === 'string'
              )
            )
          else onChange([])
        }}
      />
    )
  }
  return (
    <Form.Item
      label={
        <HelpIcon text={field.tooltip}>
          <span>
            {field.name}
            {field.type === 'comments' && (
              <span style={{ marginLeft: 8 }}>
                <CIMSBadge count={field.commentCount ?? 0} showZero />
              </span>
            )}
          </span>
        </HelpIcon>
      }
      htmlFor={field.id}
      required={!!field.required}
      validateStatus={error ? 'error' : undefined}
      help={error ? <span id={errorId}>{error}</span> : undefined}
    >
      {control}
    </Form.Item>
  )
}

export default EditFieldControl
